import React, { useCallback, useMemo, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CommonDataTable from "../../../components/CommonDataTable";
import { ORG_TYPES_MASTER, ORG_STATUS } from "./orgConstants";

function statusChip(status) {
  const s = String(status || "").trim().toLowerCase();
  const label =
    s === "active"
      ? "Active"
      : s === "inactive"
        ? "Inactive"
        : s === "blocked"
          ? "Blocked"
          : s === "archived"
            ? "Archived"
            : String(status || "—");
  let cls = "text-bg-secondary-subtle text-secondary";
  if (s === "active") cls = "text-bg-success-subtle text-success";
  else if (s === "blocked") cls = "text-bg-danger-subtle text-danger";
  return (
    <span className={`badge rounded-pill px-2 py-1 ${cls}`}>
      {label}
    </span>
  );
}

export default function OrganizationLanding({
  orgFlat,
  onCreate,
  onView,
  onEdit,
  onDeleteRequest,
  onManualRefresh,
  showCreateButton = true,
  createButtonText = "Add New Organization",
  createButtonIcon = "bi-plus-circle",
  createButtonVariant = "contained",
  createButtonColor = "primary",
}) {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const parentLabel = useCallback(
    (parentId) => {
      if (!parentId) return "—";
      const p = orgFlat.find((o) => o.id === parentId);
      return p ? `${p.code} — ${p.name}` : parentId;
    },
    [orgFlat]
  );

  const rows = useMemo(
    () =>
      (orgFlat || []).map((o) => ({
        id: o.id,
        organizationCode: o.code,
        organizationName: o.name,
        type: o.type,
        parentOrganization: parentLabel(o.parentId),
        status: o.status,
      })),
    [orgFlat, parentLabel]
  );

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleView = () => {
    if (menuRow) onView(menuRow.id);
    handleCloseMenu();
  };

  const handleEdit = () => {
    if (menuRow) onEdit(menuRow.id);
    handleCloseMenu();
  };

  const handleDelete = () => {
    if (menuRow) onDeleteRequest(menuRow.id);
    handleCloseMenu();
  };

  const allPossibleColumns = useMemo(
    () => [
      {
        field: "__actions",
        headerName: "",
        width: 20,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleOpenMenu(e, params.row)} aria-label="Row actions">
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
      { field: "organizationCode", headerName: "Organization code", width: 160 },
      { field: "organizationName", headerName: "Organization name", width: 220 },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        type: "singleSelect",
        valueOptions: ORG_TYPES_MASTER.map((t) => t.value),
      },
      { field: "parentOrganization", headerName: "Parent organization", width: 280 },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        type: "singleSelect",
        valueOptions: ORG_STATUS.map((x) => x.value),
        renderCell: (params) => statusChip(params.value),
      },
    ],
    []
  );

  const initialVisible = useMemo(
    () => allPossibleColumns.map((c) => c.field),
    [allPossibleColumns]
  );

  return (
    <>
      <CommonDataTable
        id="organization-master-table"
        allPossibleColumns={allPossibleColumns}
        initialVisible={initialVisible}
        initialRows={rows}
        height={500}
        rowsPerPageOptions={[10, 25, 50, 100]}
        checkboxSelection
        exportImportPath="organization"
        sampleFileUrl="/samples/organization.xlsx"
        sampleFileName="organization"
        showCreateButton={showCreateButton}
        createButtonText={createButtonText}
        createButtonIcon={createButtonIcon}
        createButtonVariant={createButtonVariant}
        createButtonColor={createButtonColor}
        onCreateClick={onCreate}
        enableImportButton={false}
        forInventory
        onManualRefresh={typeof onManualRefresh === "function" ? onManualRefresh : () => Promise.resolve()}
        onDataRefresh={async () => {}}
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={handleView}>
          <i className="bi bi-eye me-2" />
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <i className="bi bi-pencil me-2" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <i className="bi bi-trash me-2" />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
}
